// The lock state and the score state of a tournament (#664, F06).
//
// This file is the ONE place in the frontend that derives the lock and score
// facts that the tournament page shows. The server decides every action. The
// page only follows it. A component asks a function and does not combine the
// flags by hand:
//   registrationLocked(t)      a new registration and a confirm are refused for
//                              everyone but an admin: registration_locked or
//                              is_locked.
//   coachEditsLocked(t)        a coach is refused: coach_edits_locked,
//                              registration_locked or is_locked. The mirror of
//                              backend/src/lib/tournamentLocks.ts.
//   boutScoreState(bouts)      how many bouts are scored, and how many are not.
//   managesUnlocked(t, manages)
//                              the manager may change the bouts: the manager of
//                              THIS tournament, and the bout list is not locked
//                              (is_locked).
//   canDeleteTournament(...)   the delete of DELETE /api/tournaments/:id: an
//                              admin always; any other caller only while the
//                              bout list is not locked AND no bout is scored.
//
// These readers keep the raw flags on purpose. The server asks for one flag, or
// the screen shows one flag: the lock switches and the state pills of
// TournamentDetail; the Weigh-in tab (it opens under is_locked, because the
// bouts are final); the removal of a registration in RegistrationsTab (the
// server reads registration_locked alone); TournamentCard (it reads the
// is_tournament_manager of a list row); and the print list of BoutsTab (an
// outer bouts.length guard stands before its all-scored test).
//
// e2e/tests/tournament-state-664.spec.ts fails on a copy of these rules in any
// other file, and it pins the coach lock against the backend file.

/** The flags that stop a coach from changing a registration. The same list as
 *  COACH_EDIT_LOCK_FLAGS in backend/src/lib/tournamentLocks.ts. */
export const COACH_EDIT_LOCK_FLAGS = ['coach_edits_locked', 'registration_locked', 'is_locked'] as const;

/** The flags that stop a new registration and a confirm (admin excepted). */
export const REGISTRATION_LOCK_FLAGS = ['registration_locked', 'is_locked'] as const;

export interface TournamentLocks {
  coach_edits_locked?: boolean | null;
  registration_locked?: boolean | null;
  is_locked?: boolean | null;
}

export function registrationLocked(t: TournamentLocks | null | undefined): boolean {
  return !!t && REGISTRATION_LOCK_FLAGS.some(flag => t[flag]);
}

export function coachEditsLocked(t: TournamentLocks | null | undefined): boolean {
  return !!t && COACH_EDIT_LOCK_FLAGS.some(flag => t[flag]);
}

export interface BoutScoreState {
  total: number;
  scored: number;
  /** The bouts that are not scored yet (pending or current). */
  open: number;
  /** The tournament has bouts and every one is scored. */
  all: boolean;
  /** At least one bout is scored. */
  any: boolean;
}

export function boutScoreState(bouts: ReadonlyArray<{ status: string }>): BoutScoreState {
  const total = bouts.length;
  const scored = bouts.filter(b => b.status === 'scored').length;
  return { total, scored, open: total - scored, all: total > 0 && scored === total, any: scored > 0 };
}

/** The manager of THIS tournament acts, and is_locked is not set. The name says
 *  which lock it reads: is_locked alone, not registration_locked. */
export function managesUnlocked(t: Pick<TournamentLocks, 'is_locked'> | null | undefined, managesThisTournament: boolean): boolean {
  return managesThisTournament && !t?.is_locked;
}

/** The two arms of the server rule: 403 TOURNAMENT_LOCKED_ADMIN_DELETE under
 *  is_locked, and 409 TOURNAMENT_HAS_RESULTS after a scored bout. */
export function canDeleteTournament(
  t: Pick<TournamentLocks, 'is_locked'> | null | undefined,
  bouts: ReadonlyArray<{ status: string }>,
  isAdmin: boolean,
): boolean {
  return isAdmin || (!t?.is_locked && !boutScoreState(bouts).any);
}
